import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { Outlet, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { getAccOrigin } from '@adsk/acs-sc-web-platform-api';
import {
  NAVIGATION_HOST_MESSAGE_EVENT_NAME,
  NavigationData,
  sendRuntimeDataEvent,
} from '@adsk/exo-skeleton-api';

import { Route } from '../../envs/types';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import getCurrentRoute from '../../utils/getCurrentRoute';
import isExoRoute from '../../utils/isExoRoute';
import {
  NAVIGATE_EVENT_NAME,
  NavigateEventDetail,
} from '../../utils/navigation';

export const NAVIGATION_SOURCE_TYPES = {
  REACT_ROUTER_DOM: 'REACT_ROUTER_DOM',
} as const;

type NavigationSource =
  (typeof NAVIGATION_SOURCE_TYPES)[keyof typeof NAVIGATION_SOURCE_TYPES];

export function extractSource(
  data: NavigationData & { _source?: NavigationSource },
) {
  return data._source;
}

export const checkExcludedPath = (
  to: string,
  currentRoute: Route | undefined,
) => {
  return currentRoute?.excludeSubPaths?.some((path) => matchPath(path, to));
};

export const redirectExcludedPath = (to: string) => {
  window.location.assign(getAccOrigin() + to);
};

/**
 * This hook is responsible for emitting navigation events to the Exo Skeleton API when we detect a navigation from the React Router DOM.
 * This will allow teams that does not use "react-router-dom@^6.0.0" to listen to navigation events.
 */
const useReactRouterDomExoNavigationEmitter = () => {
  const location = useLocation();

  useEffect(() => {
    const to = location.pathname + location.search + location.hash;
    void sendRuntimeDataEvent(NAVIGATION_HOST_MESSAGE_EVENT_NAME, {
      to,
      _source: NAVIGATION_SOURCE_TYPES.REACT_ROUTER_DOM,
    });
  }, [location]);
};

const useExoNavigationHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRoute = useMemo(() => getCurrentRoute(location), [location]);

  const handleNavigateRequest = useCallback(
    ({ detail: { to, source } }: CustomEvent<NavigateEventDetail>) => {
      if (!to) {
        return;
      }

      if (checkExcludedPath(to, currentRoute)) {
        /**
         * This is responsible for checking if the current route is an excluded path.
         * If it is, we will redirect the user to the ACC hostname + pathname.
         * We should consider removing this once /model/members is onboarded to ExoSkeleton.
         * https://jira.autodesk.com/browse/WPX-1716
         */
        redirectExcludedPath(to);
        return;
      }

      if (source === NAVIGATION_SOURCE_TYPES.REACT_ROUTER_DOM) {
        /**
         * We don't want to navigate to the same URL emitted by "react-router-dom", otherwise we will have an infinite loop.
         */
        return;
      }

      if (isExoRoute(to)) {
        const urlWithoutOrigin = to.replace(window.location.origin, '');
        navigate(urlWithoutOrigin);
      } else {
        window.location.assign(to);
      }
    },
    [navigate, currentRoute],
  );

  useEffect(() => {
    window.addEventListener(NAVIGATE_EVENT_NAME, handleNavigateRequest);
    return () => {
      window.removeEventListener(NAVIGATE_EVENT_NAME, handleNavigateRequest);
    };
  }, [handleNavigateRequest]);
};

const ExoSkeletonApi: FC = () => {
  useReactRouterDomExoNavigationEmitter();
  useExoNavigationHandler();
  useNetworkStatus();

  return <Outlet />;
};

export default ExoSkeletonApi;
